import { cn } from 'cn';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useSession } from '@/app/session';
import { Skeleton } from '@/components/ui/skeleton';
import { ActingAsBanner } from './ActingAsBanner';
import { BottomBar, MenuSheet } from './BottomBar';
import { FooterLine } from './FooterLine';
import { MatterSearch } from './MatterSearch';
import { ShellProvider, WIDTH_CLASS, useShell } from './context';
import { OfflineBanner, RouteAnnouncer, SessionExpiredDialog, SkipLink } from './states';
import { TopBar } from './TopBar';

/** Boot: chrome renders immediately with a content skeleton; no flash of the login page. */
function BootSkeleton() {
  return (
    <div role="status" aria-busy="true" aria-label="Loading" className="flex flex-col gap-4 pt-6">
      <Skeleton className="h-8 w-56" />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-[67px] rounded-xl" />)}
      </div>
      <div className="flex flex-col gap-px overflow-hidden rounded-xl border border-beam-line">
        {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-[46px] rounded-none" />)}
      </div>
    </div>
  );
}

function Frame() {
  const { status } = useSession();
  const { width } = useShell();
  return (
    <div className="flex min-h-svh flex-col">
      <SkipLink />
      <TopBar />
      <ActingAsBanner />
      <OfflineBanner />
      <main id="content" tabIndex={-1} className={cn('mx-auto w-full flex-1 px-4 pb-20 outline-none md:px-6 md:pb-8', WIDTH_CLASS[width])}>
        {status === 'loading' ? <BootSkeleton /> : <Outlet />}
      </main>
      <FooterLine className="pb-20 md:pb-4" />
      <BottomBar />
      <MenuSheet />
      <MatterSearch />
      <SessionExpiredDialog />
      <RouteAnnouncer />
    </div>
  );
}

/**
 * The authenticated frame. An unauthenticated session goes to /login with
 * the deep link preserved; a loading one renders the chrome with skeletons.
 */
export function AppShell() {
  const { status } = useSession();
  const location = useLocation();
  if (status === 'unauthenticated') {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return (
    <ShellProvider>
      <Frame />
    </ShellProvider>
  );
}
